import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Filter, RefreshCw, Search, X } from 'lucide-react';
import auditLogService from '../../services/auditLogService';
import adminService from '../../services/adminService';
import { useLanguage } from '../../context/LanguageContext';
import { ASSIGNABLE_ROLES, ROLE_LABELS } from '../../utils/rbac';
import {
  AUDIT_ACTIONS,
  AUDIT_TARGET_MODELS,
  getActionVerb,
  getActorFilterPath,
  getActorProfilePath,
  getAuditActor,
  getEntityAdminPath,
  getTargetModelLabel,
} from '../../utils/auditLogFormatters';

const formatUserOptionLabel = (u) =>
  u.fullName ||
  `${u.firstName || ''} ${u.lastName || ''}`.trim() ||
  u.email ||
  String(u._id);

/**
 * Phrase interactive : [Acteur] a [action] le [modèle] [entité]
 */
const AuditLogDescription = ({ log, t }) => {
  const actor = getAuditActor(log);
  const actorName = actor.name || t('auditLog.unknownUser') || 'Utilisateur inconnu';
  const actorFilterPath = getActorFilterPath(actor.id, actor.role);
  const actorProfilePath = getActorProfilePath(actor.id);
  const verb = getActionVerb(log.action, t);
  const modelLabel = getTargetModelLabel(log.targetModel, t);
  const entityName =
    log.targetLabel ||
    (log.targetId ? `#${String(log.targetId).slice(-6)}` : null);
  const entityPath = getEntityAdminPath(log.targetModel, log.targetId);

  return (
    <span className="text-sm text-gray-800 leading-relaxed">
      {actor.id ? (
        <Link
          to={actorFilterPath}
          className="font-semibold text-blue-700 hover:underline"
          title={t('auditLog.filterByUser') || 'Voir les actions de cet utilisateur'}
        >
          {actorName}
        </Link>
      ) : (
        <span className="font-semibold text-gray-700">{actorName}</span>
      )}
      {' '}
      <span>{verb}</span>
      {' '}
      <span>{modelLabel}</span>
      {entityName ? (
        <>
          {' '}
          {entityPath ? (
            <Link
              to={entityPath}
              className="font-semibold text-emerald-700 hover:underline"
              title={t('auditLog.openEntity') || "Ouvrir l'entité"}
            >
              {entityName}
            </Link>
          ) : (
            <span className="font-semibold text-gray-700">{entityName}</span>
          )}
        </>
      ) : null}
      {actor.id && actorProfilePath ? (
        <>
          {' '}
          <Link
            to={actorProfilePath}
            className="text-xs text-gray-400 hover:text-blue-600 hover:underline"
          >
            ({t('auditLog.viewProfile') || 'profil'})
          </Link>
        </>
      ) : null}
    </span>
  );
};

const AuditLogsPage = () => {
  const { t } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 20,
  });
  const [showFilters, setShowFilters] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [userOptions, setUserOptions] = useState([]);
  const [loadingUserOptions, setLoadingUserOptions] = useState(false);
  const resolvedActorFromUrlRef = useRef(null);

  const [filters, setFilters] = useState({
    page: Number(searchParams.get('page') || 1),
    limit: 20,
    search: searchParams.get('search') || '',
    action: searchParams.get('action') || '',
    targetModel: searchParams.get('targetModel') || '',
    filterRole: searchParams.get('filterRole') || '',
    userId: searchParams.get('userId') || '',
    startDate: searchParams.get('startDate') || '',
    endDate: searchParams.get('endDate') || '',
  });

  const [searchInput, setSearchInput] = useState(filters.search);

  // Sync URL → filters (deep-link userId / etc.)
  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      page: Number(searchParams.get('page') || 1),
      search: searchParams.get('search') || '',
      action: searchParams.get('action') || '',
      targetModel: searchParams.get('targetModel') || '',
      filterRole: searchParams.get('filterRole') || '',
      userId: searchParams.get('userId') || '',
      startDate: searchParams.get('startDate') || '',
      endDate: searchParams.get('endDate') || '',
    }));
    setSearchInput(searchParams.get('search') || '');
  }, [searchParams]);

  const syncUrl = useCallback(
    (next) => {
      const params = {};
      if (next.page && next.page > 1) params.page = String(next.page);
      if (next.search) params.search = next.search;
      if (next.action) params.action = next.action;
      if (next.targetModel) params.targetModel = next.targetModel;
      if (next.filterRole) params.filterRole = next.filterRole;
      if (next.userId) params.userId = next.userId;
      if (next.startDate) params.startDate = next.startDate;
      if (next.endDate) params.endDate = next.endDate;
      setSearchParams(params);
    },
    [setSearchParams]
  );

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const response = await auditLogService.getAuditLogs({
        page: filters.page,
        limit: filters.limit,
        search: filters.search || undefined,
        action: filters.action || undefined,
        targetModel: filters.targetModel || undefined,
        userId: filters.userId || undefined,
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
      });
      setLogs(response.data || []);
      setPagination(
        response.pagination || {
          currentPage: filters.page,
          totalPages: 1,
          totalItems: (response.data || []).length,
          itemsPerPage: filters.limit,
        }
      );
    } catch (err) {
      console.error('Failed to fetch audit logs', err);
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await auditLogService.getAuditLogStats();
      setStats(response.data || null);
    } catch (err) {
      console.error('Failed to fetch audit stats', err);
    }
  }, []);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // Deep-link ?userId= sans filterRole : déduire le rôle pour le filtre en cascade
  useEffect(() => {
    const uid = filters.userId;
    if (!uid || filters.filterRole) return;
    if (resolvedActorFromUrlRef.current === uid) return;
    resolvedActorFromUrlRef.current = uid;

    (async () => {
      try {
        const response = await adminService.getUserDetails(uid);
        const role = response.data?.user?.role;
        if (role) {
          setFilters((prev) => {
            const next = { ...prev, filterRole: role };
            syncUrl(next);
            return next;
          });
          setShowFilters(true);
        }
      } catch (err) {
        console.error('Could not resolve actor role from userId', err);
      }
    })();
  }, [filters.userId, filters.filterRole, syncUrl]);

  // Charger les utilisateurs du rôle sélectionné
  useEffect(() => {
    if (!filters.filterRole) {
      setUserOptions([]);
      return undefined;
    }

    let cancelled = false;
    setLoadingUserOptions(true);
    adminService
      .getUsers({
        role: filters.filterRole,
        limit: 500,
        minimal: true,
        sortBy: 'fullName',
        sortOrder: 'asc',
      })
      .then((response) => {
        if (!cancelled) {
          setUserOptions(response.data || []);
        }
      })
      .catch((err) => {
        console.error('Failed to load users for audit filter', err);
        if (!cancelled) setUserOptions([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingUserOptions(false);
      });

    return () => {
      cancelled = true;
    };
  }, [filters.filterRole]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput === filters.search) return;
      const next = { ...filters, search: searchInput, page: 1 };
      setFilters(next);
      syncUrl(next);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]); // eslint-disable-line react-hooks/exhaustive-deps

  const topActions = useMemo(() => {
    if (!stats?.actions) return [];
    return Object.entries(stats.actions)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);
  }, [stats]);

  const updateFilter = (key, value) => {
    const next = { ...filters, [key]: value, page: key === 'page' ? value : 1 };
    setFilters(next);
    syncUrl(next);
  };

  const handleFilterRoleChange = (role) => {
    resolvedActorFromUrlRef.current = null;
    const next = {
      ...filters,
      filterRole: role,
      userId: '',
      page: 1,
    };
    setFilters(next);
    syncUrl(next);
  };

  const handleFilterUserChange = (userId) => {
    const next = { ...filters, userId, page: 1 };
    setFilters(next);
    syncUrl(next);
  };

  const selectedUserLabel = useMemo(() => {
    if (!filters.userId) return null;
    const found = userOptions.find((u) => String(u._id) === String(filters.userId));
    if (found) return formatUserOptionLabel(found);
    return filters.userId;
  }, [filters.userId, userOptions]);

  const clearFilters = () => {
    resolvedActorFromUrlRef.current = null;
    const next = {
      page: 1,
      limit: 20,
      search: '',
      action: '',
      targetModel: '',
      filterRole: '',
      userId: '',
      startDate: '',
      endDate: '',
    };
    setSearchInput('');
    setUserOptions([]);
    setFilters(next);
    setSearchParams({});
  };

  const hasActiveFilters = Boolean(
    filters.search ||
      filters.action ||
      filters.targetModel ||
      filters.filterRole ||
      filters.userId ||
      filters.startDate ||
      filters.endDate
  );

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {t('sidebar.admin.auditLogs') || "Historique d'audit"}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {t('auditLog.subtitle') ||
              'Suivi des actions utilisateurs, vendeurs et administrateurs'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowFilters((v) => !v)}
            className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm hover:bg-gray-50"
          >
            <Filter size={16} />
            {t('auditLog.filters') || 'Filtres'}
          </button>
          <button
            type="button"
            onClick={() => {
              fetchLogs();
              fetchStats();
            }}
            className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm hover:bg-gray-50"
          >
            <RefreshCw size={16} />
            {t('common.refresh') || 'Actualiser'}
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
          <p className="text-xs uppercase tracking-wide text-gray-500">
            {t('auditLog.totalLogs') || 'Total des logs'}
          </p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{stats?.total ?? '—'}</p>
        </div>
        {topActions.slice(0, 3).map(([action, count]) => (
          <button
            key={action}
            type="button"
            onClick={() => updateFilter('action', action)}
            className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm text-left hover:border-blue-200"
          >
            <p className="text-xs uppercase tracking-wide text-gray-500 truncate">
              {getActionVerb(action, t)}
            </p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{count}</p>
          </button>
        ))}
      </div>

      {/* Search + filters */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm mb-4 p-4">
        <div className="flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={t('auditLog.searchPlaceholder') || 'Rechercher (détails, entité, action…)'}
              className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          {hasActiveFilters && (
            <button
              type="button"
              onClick={clearFilters}
              className="inline-flex items-center gap-1 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg"
            >
              <X size={14} />
              {t('auditLog.clearFilters') || 'Réinitialiser'}
            </button>
          )}
        </div>

        {/* Filtre en cascade : rôle → utilisateur (acteur des logs) */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4 pt-4 border-t border-gray-100">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">
              {t('auditLog.filterRole') || "Rôle de l'acteur"}
            </label>
            <select
              value={filters.filterRole}
              onChange={(e) => handleFilterRoleChange(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
            >
              <option value="">{t('auditLog.allRoles') || 'Tous les rôles'}</option>
              {ASSIGNABLE_ROLES.map((role) => (
                <option key={role} value={role}>
                  {ROLE_LABELS[role] || role}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">
              {t('auditLog.filterUser') || 'Utilisateur (acteur)'}
            </label>
            <select
              value={filters.userId}
              onChange={(e) => handleFilterUserChange(e.target.value)}
              disabled={!filters.filterRole || loadingUserOptions}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm disabled:bg-gray-50 disabled:text-gray-400"
            >
              <option value="">
                {!filters.filterRole
                  ? t('auditLog.selectRole') || 'Choisir un rôle…'
                  : loadingUserOptions
                    ? t('auditLog.loadingUsers') || 'Chargement…'
                    : t('auditLog.selectUser') || 'Choisir un utilisateur…'}
              </option>
              {userOptions.map((u) => (
                <option key={u._id} value={u._id}>
                  {formatUserOptionLabel(u)}
                  {u.email ? ` (${u.email})` : ''}
                </option>
              ))}
            </select>
          </div>
          {filters.userId ? (
            <div className="md:col-span-2 text-sm text-blue-700 bg-blue-50 rounded-lg px-3 py-2 flex flex-wrap items-center gap-2">
              <span>
                {t('auditLog.filteredByUser') || 'Filtré par utilisateur'} :{' '}
                <strong>{selectedUserLabel}</strong>
              </span>
              <button
                type="button"
                onClick={() => handleFilterUserChange('')}
                className="text-xs text-blue-800 underline hover:no-underline"
              >
                {t('auditLog.clearUserFilter') || 'Retirer'}
              </button>
            </div>
          ) : null}
        </div>

        {showFilters && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-4 pt-4 border-t border-gray-100">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">
                {t('auditLog.action') || 'Action'}
              </label>
              <select
                value={filters.action}
                onChange={(e) => updateFilter('action', e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
              >
                <option value="">{t('auditLog.allActions') || 'Toutes'}</option>
                {AUDIT_ACTIONS.map((action) => (
                  <option key={action} value={action}>
                    {getActionVerb(action, t)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">
                {t('auditLog.targetModel') || 'Modèle'}
              </label>
              <select
                value={filters.targetModel}
                onChange={(e) => updateFilter('targetModel', e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
              >
                <option value="">{t('auditLog.allModels') || 'Tous'}</option>
                {AUDIT_TARGET_MODELS.map((model) => (
                  <option key={model} value={model}>
                    {getTargetModelLabel(model, t)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">
                {t('auditLog.startDate') || 'Date début'}
              </label>
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => updateFilter('startDate', e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">
                {t('auditLog.endDate') || 'Date fin'}
              </label>
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => updateFilter('endDate', e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
              />
            </div>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        {loading ? (
          <p className="p-6 text-gray-500">{t('common.loading') || 'Chargement...'}</p>
        ) : logs.length === 0 ? (
          <p className="p-6 text-gray-500">{t('auditLog.empty') || 'Aucun log trouvé.'}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="py-3 px-4 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                    {t('auditLog.description') || 'Description'}
                  </th>
                  <th className="py-3 px-4 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                    {t('auditLog.action') || 'Action'}
                  </th>
                  <th className="py-3 px-4 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                    {t('auditLog.timestamp') || 'Horodatage'}
                  </th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => {
                  const isExpanded = expandedId === log._id;
                  const hasDetails = Boolean(log.details || (log.changes && Object.keys(log.changes).length));
                  return (
                    <React.Fragment key={log._id}>
                      <tr className="border-b border-gray-50 hover:bg-gray-50/80">
                        <td className="py-3 px-4 align-top max-w-xl">
                          <AuditLogDescription log={log} t={t} />
                          {hasDetails ? (
                            <button
                              type="button"
                              onClick={() => setExpandedId(isExpanded ? null : log._id)}
                              className="mt-1 block text-xs text-blue-600 hover:underline"
                            >
                              {isExpanded
                                ? t('auditLog.hideDetails') || 'Masquer les détails'
                                : t('auditLog.showDetails') || 'Voir les détails'}
                            </button>
                          ) : null}
                        </td>
                        <td className="py-3 px-4 align-top">
                          <span className="inline-flex px-2 py-1 rounded-md bg-gray-100 text-xs font-mono text-gray-700">
                            {log.action}
                          </span>
                        </td>
                        <td className="py-3 px-4 align-top text-sm text-gray-600 whitespace-nowrap">
                          {log.createdAt
                            ? new Date(log.createdAt).toLocaleString()
                            : '—'}
                        </td>
                      </tr>
                      {isExpanded && hasDetails ? (
                        <tr className="bg-slate-50 border-b border-gray-100">
                          <td colSpan={3} className="px-4 py-3 text-sm text-gray-700">
                            {log.details ? (
                              <p className="mb-2">
                                <span className="font-medium">
                                  {t('auditLog.details') || 'Détails'} :
                                </span>{' '}
                                {log.details}
                              </p>
                            ) : null}
                            {log.changes && Object.keys(log.changes).length > 0 ? (
                              <div>
                                <p className="font-medium mb-1">
                                  {t('auditLog.changes') || 'Modifications'} :
                                </p>
                                <ul className="space-y-1 text-xs font-mono">
                                  {Object.entries(log.changes).map(([field, change]) => (
                                    <li key={field} className="break-all">
                                      <span className="text-blue-700">{field}</span>:{' '}
                                      <span className="text-red-600">
                                        {JSON.stringify(change?.oldValue)}
                                      </span>
                                      {' → '}
                                      <span className="text-emerald-700">
                                        {JSON.stringify(change?.newValue)}
                                      </span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            ) : null}
                            {log.ipAddress ? (
                              <p className="mt-2 text-xs text-gray-500">
                                IP: {log.ipAddress}
                              </p>
                            ) : null}
                          </td>
                        </tr>
                      ) : null}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      <div className="mt-4 flex justify-between items-center">
        <button
          type="button"
          onClick={() => updateFilter('page', Math.max((filters.page || 1) - 1, 1))}
          disabled={(filters.page || 1) <= 1}
          className="px-4 py-2 bg-white border border-gray-200 rounded-lg disabled:opacity-50 text-sm"
        >
          {t('common.previous') || 'Précédent'}
        </button>
        <span className="text-sm text-gray-600">
          {t('common.page') || 'Page'} {pagination.currentPage || filters.page} /{' '}
          {pagination.totalPages || 1}
          {typeof pagination.totalItems === 'number'
            ? ` · ${pagination.totalItems} ${t('auditLog.entries') || 'entrées'}`
            : ''}
        </span>
        <button
          type="button"
          onClick={() =>
            updateFilter(
              'page',
              Math.min((filters.page || 1) + 1, pagination.totalPages || 1)
            )
          }
          disabled={(filters.page || 1) >= (pagination.totalPages || 1)}
          className="px-4 py-2 bg-white border border-gray-200 rounded-lg disabled:opacity-50 text-sm"
        >
          {t('common.next') || 'Suivant'}
        </button>
      </div>
    </div>
  );
};

export default AuditLogsPage;
